import { JSX, Match, Show, Switch, createEffect, createMemo, createSignal, onMount } from 'solid-js'
import { toast } from 'common'
import { visitorCheckin as api } from 'st-app/api'
import { requireVisitorCheckinContext } from '../VisitorCheckinContext'
import { requireStepContext } from '../../st-wizard/st-wizard'
import { Loader, Button } from 'st-app';
import { formatAddress, formatDob, formatGender as formatSex, formatHeight, formatName } from './util'
import { DenialScriptOverlay } from './denial-script-overlay'
import styles from './verification-step.module.css'

const NO_FLY_REASONS = [
    { code: '0', label: 'Custody' },
    { code: '1', label: 'Trespassing' },
    { code: '2', label: 'Past Incident' },
    { code: '3', label: 'Other' },
]

const bannerStyle = (color?: string) => {
    switch ((color || 'Red').toLowerCase()) {
        case 'green':  return { bg: '#e7f4e8', border: '#1f8b3b', icon: 'fas fa-star',                    fg: '#14571f' };
        case 'yellow': return { bg: '#fdf6e3', border: '#c79100', icon: 'fas fa-triangle-exclamation',    fg: '#7a5b00' };
        default:       return { bg: '#fde8e8', border: '#c0341d', icon: 'fas fa-ban',                     fg: '#7a1d12' }; // Red
    }
};

export const VerificationStep = () => {
    const checkin = requireVisitorCheckinContext();
    const step = requireStepContext();

    const [isProcessing, setIsProcessing] = createSignal(false);

    // Read-aloud district script shown after a deny. null = not denied yet.
    const [denialScript, setDenialScript] = createSignal<string | null>(null);

    // Terminal close for the whole check-in modal, once the clerk taps Done on
    // the script overlay. Kept in DEV so the modal stays open for inspection.
    const finishAndClose = () => {
        if (import.meta.env.DEV) {
            toast.notify('notice', '(DEV mode, modal not closed)');
            setDenialScript(null);
            return;
        }
        if (window.ST) {
            window.ST.CloseModal();
            window.ST.RefreshView();
        }
    }

    // No-Fly modal state
    const [showNoFlyModal, setShowNoFlyModal] = createSignal(false);
    const [noFlyReason, setNoFlyReason] = createSignal('0');
    const [noFlyNotes, setNoFlyNotes] = createSignal('');
    const [noFlyDistrictWide, setNoFlyDistrictWide] = createSignal(true);
    const [isAddingToNoFly, setIsAddingToNoFly] = createSignal(false);

    const openNoFlyModal = () => {
        setNoFlyReason('0');
        setNoFlyNotes('');
        setNoFlyDistrictWide(true);
        setShowNoFlyModal(true);
    }

    const confirmAddToNoFly = async () => {
        if (isAddingToNoFly()) return;
        setIsAddingToNoFly(true);
        try {
            const result = await api.AddToNoFly(checkin.vmGU()!, noFlyReason(), noFlyNotes(), noFlyDistrictWide(), []);
            if (!result.ok && !result.alreadyExists) {
                // Failed — keep the dialog open so staff can retry.
                toast.notify('error', result.message ?? 'Could not add to the Blocked / No-Fly list.');
                setIsAddingToNoFly(false);
                return;
            }

            // Added (or already on the list): this visit is now blocked — deny it so the grid reflects it.
            try { await api.DenyVisitor(checkin.vmGU()!, 'NOFLY'); } catch (e) { console.error(e); }
            toast.notify('success', result.alreadyExists
                ? 'This adult is already on the Blocked / No-Fly list — entry denied.'
                : 'Visitor added to the Blocked / No-Fly list and denied entry.');

            setShowNoFlyModal(false);
            if (import.meta.env.DEV) {
                toast.notify('notice', '(DEV mode, modal not closed)');
            } else if (window.ST) {
                window.ST.CloseModal();
                window.ST.RefreshView();
            }
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while adding to the No-Fly list.');
        }
        setIsAddingToNoFly(false);
    }

    onMount(() => {
        if (checkin.verificationStatus() === 'pending' && !checkin.isVerifying()) {
            checkin.verify();
        }
    })

    const [graceDone, setGraceDone] = createSignal(false);
    createEffect(() => {
        if (checkin.verificationStatus() === 'verified' && !offender()) {
            if (checkin.repeatTier() === 'Grace') {
                if (!graceDone()) {
                    setGraceDone(true);
                    (async () => {
                        try { await api.ReopenVisit(checkin.repeatPriorVisitGU()); }
                        catch (e) { console.error(e); }
                        if (import.meta.env.DEV) {
                            toast.notify('success', 'Grace re-entry (DEV mode, modal not closed)');
                        } else {
                            setTimeout(() => { if (window.ST) { window.ST.CloseModal(); window.ST.RefreshView(); } }, 1500);
                        }
                    })();
                }
                return;
            }
            if (!step.completed()) {
                setTimeout(() => { step.setCompleted(true); step.next(); }, 1000);
            }
        }
    })

    const offender = createMemo(() => checkin.offenders()?.at(0))


    const confirmMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            const resp = await api.DenyVisitor(checkin.vmGU()!, 'OFFENDER');
            // Deny recorded + notifications sent server-side; show the district
            // script for the clerk to read aloud, then finishAndClose on Done.
            setDenialScript(resp?.message || 'This visitor has been denied entry.');
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while denying the visitor.');
        }
        setIsProcessing(false);
    }

    const rejectMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            await api.ApproveVisitor(checkin.vmGU()!);
            checkin.setVerificationStatus('verified');
            step.setCompleted(true);
            step.next();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while clearing the visitor.');
        }
        setIsProcessing(false);
    }

    const notAMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            await api.ApproveVisitor(checkin.vmGU()!);
            checkin.setVerificationStatus('verified');
            checkin.setVerificationStatusMessage('Verification Complete');
            step.setCompleted(true);
            step.next();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while clearing the visitor.');
        }
        setIsProcessing(false);
    }

    const confirmBlocked = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            const match = checkin.blockedMatches()[0];
            const resp: any = await api.ConfirmBlockedDeny(checkin.vmGU()!, match?.vmBlockedVisitorGU!);
            // Deny recorded; show the district script to read aloud. Fall back to
            // the Block-tier message already loaded during verify() if the deny
            // response carries none.
            const script = (resp?.message as string) || checkin.verificationStatusMessage() || '';
            setDenialScript(script || 'This visitor has been denied entry.');
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while denying the visitor.');
        }
        setIsProcessing(false);
    }

    const allowAndNotify = async () => {
        if (isProcessing()) return;
        setIsProcessing(true);
        try {
            const entry = checkin.blockedMatches()[0];
            await api.ConfirmWatchAllow(checkin.vmGU()!, entry?.vmBlockedVisitorGU!);
            toast.notify('success', 'Visitor allowed. Designated staff have been notified.');
            checkin.setVerificationStatus('verified');
            step.setCompleted(true);
            step.next();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while allowing the visitor.');
        }
        setIsProcessing(false);
    };

    return (
        <div class={styles['verification-step']}>
            <Switch>
                <Match when={checkin.verificationStatus() === 'verified' && checkin.repeatTier() === 'Grace'}>
                    <div style="display:flex; flex-direction:column; align-items:center; gap:8px; padding:24px;">
                        <i class="fas fa-circle-check" style="font-size:2.5em; color: var(--st-success-color);" />
                        <span style="font-size:1.4em; font-weight:bold;">You're still checked in — welcome back</span>
                        <span style="opacity:0.8;">No new badge needed. Your visit has been re-activated.</span>
                    </div>
                </Match>
                <Match when={checkin.verificationStatus() === 'verified' && !offender()}>
                    <span role="presentation" class={styles['verified']}>
                        <svg width="128" height="128" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle
                                class={`${styles['draw-path']} ${styles['circle']}`}
                                pathLength={1}
                                cx="24"
                                cy="24"
                                r="20"
                                fill="transparent"
                                stroke-width="4"
                                stroke="currentColor"
                            />
                            <path
                                class={`${styles['draw-path']} ${styles['check']}`}
                                pathLength={1}
                                d="M16 24 L22 30 L32 20"
                                stroke="currentColor"
                                stroke-width="4"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    {checkin.verificationStatusMessage()}
                </Match>
                <Match when={checkin.verificationStatus() === 'dangerous'}>
                    {(() => {
                        const entry = checkin.blockedMatches()[0];
                        const disp = (entry?.disposition || 'BLOCK').toUpperCase();
                        const bs = bannerStyle(entry?.color);
                        return (
                            <div class={styles['danger-banner']} style={`background: ${bs.bg}; border-color: ${bs.border}; color: ${bs.fg};`}>
                                    <i class={bs.icon}/>
                                    <Show
                                        when={checkin.blockedMatches().length > 0}
                                        fallback={
                                            <div style='display: flex; flex-direction: column; align-items: center; font-size: 0.7em;'>
                                                <span style='font-size: 1em; font-weight: bold;'>The scanned ID was flagged as a potential match with a known offender.</span>
                                                <div style='font-size: 0.8em; font-style: italic; opacity: 0.9;'>
                                                    Possible match determined based on First Name, Last Name, and Date of Birth.
                                                </div>
                                            </div>
                                        }
                                    >
                                        <span style='font-size: 1.6em; font-weight: bold; text-align: center;'>
                                            {disp === 'NOTIFY' ? (entry?.reasonDescription || 'High-Profile Visitor') : 'Visitor Blocked'}
                                        </span>
                                    </Show>
                                    <i class={bs.icon}/>
                            </div>
                        );
                    })()}
                </Match>
                <Match when={checkin.verificationStatus() === 'review'}>
                    <div class={styles['danger-banner']}>
                            <i class={`fas fa-exclamation-triangle`}/>
                            <div style='display: flex; flex-direction: column; align-items: center; font-size: 0.7em;'>
                                <span style='font-size: 1em; font-weight: bold;'>{checkin.verificationStatusMessage()}</span>
                                <div style='font-size: 0.8em; font-style: italic; opacity: 0.9;'>
                                    Please review the potential match below and confirm or dismiss.
                                </div>
                            </div>
                            <i class={`fas fa-exclamation-triangle`}/>
                    </div>
                </Match>
                <Match when={checkin.verificationStatus() === 'pending' || checkin.isVerifying()}>
                    <Loader size='large'/>
                    {checkin.verificationStatusMessage()}
                </Match>
            </Switch>

            <Show when={checkin.offenders().length > 0}>
                <div class={styles['offender-comparison']}>
                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Scanned Info</span>

                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <Show when={checkin.scan()?.photoBase64}
                                      fallback={<div class={styles['comparison-photo-placeholder']}>No photo</div>}>
                                    <img src={`data:image/bmp;base64,${checkin.scan()!.photoBase64}`}></img>
                                </Show>
                            </div>
                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{formatName(checkin.scan()?.AAMVA)}</span>
                                <Field label="Date of Birth" value={formatDob(checkin.scan()?.AAMVA?.dateOfBirth)} />
                                <Field label="Sex" value={formatSex(checkin.scan()?.AAMVA?.sex)} />
                                <Field label="Hair Color" value={offender()?.hairColor} />
                                <Field label="Eye Color" value={offender()?.eyeColor} />
                                <Field label="Height" value={formatHeight(offender()?.height)} />
                                <Field label="Address" value={formatAddress(offender())} />
                            </div>
                        </div>

                        <div class={styles['drivers-license']}>
                            <Show when={checkin.scan()?.idBase64}
                                  fallback={<div class={styles['comparison-photo-placeholder']}>No ID image</div>}>
                                <img style={{
                                    'aspect-ratio': '3.375/2.125',
                                    'border-radius': '8px',
                                    height: '200px',
                                }} src={`data:image/bmp;base64,${checkin.scan()!.idBase64}`}></img>
                            </Show>
                        </div>
                    </div>

                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Potential Match</span>
                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <img src={offender()?.offenderImageUrl} />
                            </div>

                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{offender()?.name}</span>

                                <Field label="Date of Birth" value={formatDob(offender()?.dob)} />
                                <Field label="Sex" value={formatSex(offender()?.sex)} />
                                <Field label="Hair Color" value={offender()?.hairColor} />
                                <Field label="Eye Color" value={offender()?.eyeColor} />
                                <Field label="Height" value={formatHeight(offender()?.height)} />
                                <Field label="Address" value={formatAddress(offender())} />
                            </div>

                        </div>

                        <div class={styles['offender-actions']}>
                            <Button icon='fas fa-bell-slash' type='success' onClick={rejectMatch}>False Alarm</Button>
                            <Button icon='fas fa-siren-on' type='danger' onClick={confirmMatch}>Confirm Match – Deny & Show Script</Button>
                        </div>
                    </div>
                </div>
            </Show>

            <Show when={checkin.blockedMatches().length > 0}>
                <div class={styles['offender-comparison']}>
                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Scanned Info</span>

                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <Show when={checkin.scan()?.photoBase64}
                                      fallback={<div class={styles['comparison-photo-placeholder']}>No photo</div>}>
                                    <img src={`data:image/bmp;base64,${checkin.scan()!.photoBase64}`}></img>
                                </Show>
                            </div>
                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{formatName(checkin.scan()?.AAMVA)}</span>
                                <Field label="Date of Birth" value={formatDob(checkin.scan()?.AAMVA?.dateOfBirth)} />
                                <Field label="Sex" value={formatSex(checkin.scan()?.AAMVA?.sex)} />
                            </div>
                        </div>

                        <div class={styles['drivers-license']}>
                            <Show when={checkin.scan()?.idBase64}
                                  fallback={<div class={styles['comparison-photo-placeholder']}>No ID image</div>}>
                                <img style={{
                                    'aspect-ratio': '3.375/2.125',
                                    'border-radius': '8px',
                                    height: '200px',
                                }} src={`data:image/bmp;base64,${checkin.scan()!.idBase64}`}></img>
                            </Show>
                        </div>
                    </div>

                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Blocked List Match</span>
                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{checkin.blockedMatches()[0]?.firstName} {checkin.blockedMatches()[0]?.lastName}</span>
                                <Field label="Date of Birth" value={formatDob(checkin.blockedMatches()[0]?.dateOfBirth)} />
                                <Field label="Reason" value={checkin.blockedMatches()[0]?.reasonDescription || checkin.blockedMatches()[0]?.reasonCode} />
                                <Field label="Notes" value={checkin.blockedMatches()[0]?.notes} />
                            </div>
                        </div>

                        <div class={styles['offender-actions']}>
                            {(() => {
                                const entry = checkin.blockedMatches()[0];
                                const disp = (entry?.disposition || 'BLOCK').toUpperCase();
                                return (
                                    <Show
                                        when={entry?.tier === 'Block'}
                                        fallback={<>
                                            <Button icon='fas fa-ban' type='secondary' onClick={notAMatch}>Not a Match</Button>
                                            <Show
                                                when={disp === 'NOTIFY'}
                                                fallback={<Button icon='fas fa-siren-on' type='danger' onClick={confirmBlocked}>Confirm Match – Deny & Show Script</Button>}
                                            >
                                                <Button type='success' icon='fas fa-right-to-bracket' onClick={allowAndNotify}>Allow + Notify Staff</Button>
                                            </Show>
                                        </>}
                                    >
                                        <Show
                                            when={disp === 'NOTIFY'}
                                            fallback={<Button icon='fas fa-siren-on' type='danger' onClick={confirmBlocked}>Visitor Denied - Show District Script</Button>}
                                        >
                                            <Button type='success' icon='fas fa-right-to-bracket' onClick={allowAndNotify}>Allow + Notify Staff</Button>
                                        </Show>
                                    </Show>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </Show>

            <Show when={checkin.verificationStatus() !== 'pending' && !checkin.isVerifying() && checkin.blockedMatches().length === 0}>
                <div style='display: flex; justify-content: center; width: 100%; margin-top: 8px;'>
                    <Button icon='fas fa-ban' type='secondary' onClick={openNoFlyModal}>Add to Blocked / No-Fly List</Button>
                </div>
            </Show>

            <Show when={denialScript() !== null}>
                <DenialScriptOverlay script={denialScript()!} onDone={finishAndClose} />
            </Show>

            <Show when={showNoFlyModal()}>
                <div style='position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;'>
                    <div style='background: var(--st-page-bg); border-radius: 8px; padding: 24px; min-width: 360px; max-width: 480px; display: flex; flex-direction: column; gap: 16px; box-shadow: 0 4px 32px rgba(0,0,0,0.3);'>
                        <span style='font-size: 1.1em; font-weight: bold;'>Add to Blocked / No-Fly List</span>

                        <div style='display: flex; flex-direction: column; gap: 4px;'>
                            <label style='font-size: 0.85em;'>Reason</label>
                            <select
                                style='padding: 6px 8px; border-radius: 4px; border: 1px solid var(--st-panel-border); background: var(--st-well-bg); color: var(--st-page-fg);'
                                value={noFlyReason()}
                                onchange={(e) => setNoFlyReason(e.currentTarget.value)}
                            >
                                {NO_FLY_REASONS.map(r => (
                                    <option value={r.code}>{r.label}</option>
                                ))}
                            </select>
                        </div>

                        <div style='display: flex; flex-direction: column; gap: 4px;'>
                            <label style='font-size: 0.85em;'>Notes</label>
                            <textarea
                                style='padding: 6px 8px; border-radius: 4px; border: 1px solid var(--st-panel-border); background: var(--st-well-bg); color: var(--st-page-fg); resize: vertical; min-height: 80px;'
                                value={noFlyNotes()}
                                onInput={(e) => setNoFlyNotes(e.currentTarget.value)}
                                placeholder='Optional notes...'
                            />
                        </div>

                        <div style='display: flex; align-items: center; gap: 8px;'>
                            <input
                                type='checkbox'
                                id='nf-district-wide'
                                checked={noFlyDistrictWide()}
                                onChange={(e) => setNoFlyDistrictWide(e.currentTarget.checked)}
                            />
                            <label for='nf-district-wide' style='font-size: 0.9em;'>District-Wide</label>
                        </div>

                        <div style='display: flex; justify-content: flex-end; gap: 8px;'>
                            <Button type='secondary' onClick={() => setShowNoFlyModal(false)}>Cancel</Button>
                            <Button icon='fas fa-ban' type='danger' onClick={confirmAddToNoFly}>
                                {isAddingToNoFly() ? 'Adding...' : 'Add to No-Fly List'}
                            </Button>
                        </div>
                    </div>
                </div>
            </Show>
        </div>
    )
}

const Field = (props: { label: string, value: JSX.Element | string | undefined }) => {
    return (
        <div class={styles['field']}>
            <span class={styles['label']}>{props.label}:</span>
            <span class={styles['value']}>{props.value ?? 'N/A'}</span>
        </div>
    )
}

